import { html } from '@codemirror/lang-html';
import { css } from '@codemirror/lang-css';
import { json } from '@codemirror/lang-json';
import { python } from '@codemirror/lang-python';
import { xml } from '@codemirror/lang-xml';
import { javascript } from '@codemirror/lang-javascript';
import { useEffect, useRef, useState } from 'react';
import { basicSetup } from 'codemirror';
import { Extension } from '@codemirror/state';
import { EditorView, keymap, lineNumbers } from '@codemirror/view';
import { bracketMatching } from '@codemirror/language';
import {
	defaultKeymap,
	history,
	indentWithTab,
	redo,
	undo
} from '@codemirror/commands';
import { CODE_EDITOR_SUPPORTED_LANG, Lang } from './types';

export const getLangExtensions = (lang: Lang) => {
	let langExt = [javascript({ jsx: true, typescript: true })];
	switch (lang) {
		case CODE_EDITOR_SUPPORTED_LANG.react:
			langExt = [javascript({ jsx: true, typescript: true })];
			break;
		case CODE_EDITOR_SUPPORTED_LANG.node:
		case CODE_EDITOR_SUPPORTED_LANG.javascript:
			langExt = [javascript({ jsx: false, typescript: true })];
			break;
		case CODE_EDITOR_SUPPORTED_LANG.html:
		case CODE_EDITOR_SUPPORTED_LANG.webComponents:
		case CODE_EDITOR_SUPPORTED_LANG.css:
			langExt = [html(), css(), javascript()];
			break;
		case CODE_EDITOR_SUPPORTED_LANG.xml:
			langExt = [xml()];
			break;
		case CODE_EDITOR_SUPPORTED_LANG.python:
			langExt = [python()];
			break;
		case CODE_EDITOR_SUPPORTED_LANG.json:
			langExt = [json()];
			break;
		case CODE_EDITOR_SUPPORTED_LANG.go:
			langExt = [javascript({ jsx: false, typescript: false })];
			break;
		default:
			langExt = [javascript({ jsx: true, typescript: true })];
	}
	return langExt;
};

export const useCodeMirror = (
	extensions: Extension[],
	editable: boolean,
	theme: string,
	lang: string
) => {
	const ref = useRef<Element | undefined>(undefined);
	const [view, setView] = useState<EditorView>();

	useEffect(() => {
		const editorView = new EditorView({
			extensions: [
				basicSetup,
				history(),
				keymap.of([
					...defaultKeymap,
					indentWithTab,
					{ key: 'Mod-z', run: undo, preventDefault: true },
					{ key: 'Mod-Shift-z', run: redo, preventDefault: true }
				]),
				lineNumbers(),
				bracketMatching(),
				...getLangExtensions(lang),
				...extensions
			],
			parent: ref.current
		});

		setView(editorView);

		return () => {
			editorView.destroy();
			setView(undefined);
		};
		// This is due to warning on missing dependency `extensions`
		// which should not be included in this array
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [editable, theme]);

	return { ref, view };
};

export default useCodeMirror;
